import DataGridViewModel from '@/ViewModels/Controls/DataGridViewModel';
import ComponentManager from '@/Management/ComponentManager';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import * as CoreFrameDTOs from "coreframetypings/dto";
import { IDataGridViewModelConstructorParameter, GridColumnDefinition, ICustomDataGridViewModelConstructorParameter } from '#/DefaultViewExtensionInterfaces';
import Utilities from '@/Utility/Utilities';

export default class CustomDataGridViewModel extends DataGridViewModel {

    protected readonly pdoId: string;
    protected readonly dataSourceTypeName: string;
    protected readonly dataSourceOperationName: string;
    protected readonly onBeforeColumnsComputedHandler: (columns: Array<any>) => void;
    protected readonly callStatic: boolean;
    protected resultPdoIds: KnockoutObservableArray<string>;

    constructor(params: ICustomDataGridViewModelConstructorParameter) {
        super($.extend(params,
            {
                autoInit: false
            }) as IDataGridViewModelConstructorParameter);
        this.callStatic = this.paramOrDefault(params.callStatic, true);
        this.pdoId = this.paramOrDefault(params.pdoId, '-1');
        this.onBeforeColumnsComputedHandler = params.onBeforeColumnsComputedHandler;
        this.dataSourceTypeName = params.dataSourceTypeName;
        this.dataSourceOperationName = params.dataSourceOperationName;
        this.resultPdoIds = this.observableOrDefault([]) as KnockoutObservableArray<string>;
    }

    /**
     * @override 
     */
    protected refreshGrid(): JQueryPromise<any> {
        this.isBusy(true);

        const filter = this.getFilterExpression();
        this.lastRefresh.filter = filter;

        // Methode vom Server aufrufen
        return this.callServerMethod()
            .then((operationResult: CoreFrameDTOs.IOperationResultDTO) => {
                if (this.isDisposed || !this.validateStatus(operationResult))
                    return;

                let result = null;
                try {
                    result = JSON.parse(operationResult.returnValue);
                }
                catch (e) {
                    result = {
                        UnfilteredCount: 0,
                        TotalCount: 0,
                        PDO_IDs: [],
                        Values: []
                    }
                }

                if (result.SearchObjectPositionResponse && result.SearchObjectPositionResponse.Page)
                    this.page(result.SearchObjectPositionResponse.Page + 1);

                if (!Utilities.isNullOrUndefined(result.UnfilteredCount)) {
                    this.fullExtentCount(result.UnfilteredCount.toString());
                } else {
                    this.fullExtentCount(result.TotalCount.toString());
                }

                this.realTotalRows(result.TotalCount);
                this.totalRows(result.TotalCount);

                this.correctPage();

                this.resultPdoIds(result.PDO_IDs || []);

                const data = this.convertArrayOfGeneric(result.Values);
                this.data(data);
            }
            ).always(() => {
                if (!this.isDisposed)
                    this.isBusy(false);
            }).promise();
    }

    /**
     * Korrigiert die aktuelle Seitenauswahl
     */
    private correctPage(): void {
        const start = this.calcStartPoint(this.page(), this.pageSize());
        let calculatedPage = (start / this.pageSize()) + 1;
        // Darf niemals kleiner 1 sein
        calculatedPage = Math.max(1, calculatedPage);

        if (this.page() !== calculatedPage) {
            this.page(calculatedPage);
            this.refreshGrid();
        }
    }

    private callServerMethod(): JQueryPromise<CoreFrameDTOs.IOperationResultDTO> {
        if (this.callStatic) {
            return RADEnvironmentProxy.getInstance().callStaticOperationWithoutTransaction({
                pdTypeName: this.dataSourceTypeName,
                operationName: this.dataSourceOperationName,
                operationParameters: this.createDataSourceOperationParameters(),
                contextParameters: {}
            }).promise();
        } else {
            return RADEnvironmentProxy.getInstance().callOperation({
                pdoID: this.pdoId,
                contextParameters: {},
                operationName: this.dataSourceOperationName,
                operationParameters: this.createDataSourceOperationParameters()
            }).promise();
        }
    }

    /**
     * Erzeugt die Parameter, die für die aufzurufende Operation benötigt werden
     */
    protected createDataSourceOperationParameters(): Array<Object> {
        const start = this.calcStartPoint(this.page(), this.pageSize());
        const end = this.calcEndPoint(start, this.pageSize());

        this.correctPage();

        const orderBy = this.getOrderExpression();

        const filter = this.getFilterExpression();
        const stringifiedFilter = !_.isEmpty(filter) ? JSON.stringify(filter) : '';

        return [
            {
                PrincipalIndependent: false,
                Properties: this.getProperties(),
                OrderBy: orderBy,
                Filter: stringifiedFilter,
                Start: start,
                End: end,
                PdoIDToFind: this.initial,
                CurrentPagingSize: this.pageSize()
            }
        ];
    }

    /**
     * @override 
     * @param columns
     */
    protected onBeforeColumnsComputed(columns: Array<GridColumnDefinition>): void {
        if (typeof this.onBeforeColumnsComputedHandler === 'function')
            this.onBeforeColumnsComputedHandler(columns);
    }
}

ComponentManager.setViewModel('CustomDataGridViewModel', CustomDataGridViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');